import React, { useRef, useEffect, useCallback, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import { useCatalogo } from '../../../../catalogo/hooks/useCatalogo';
import { CatalogoCard } from '../../../../catalogo/components/CatalogoCard';
import { FiltrosPanel } from '../../../../catalogo/components/FiltrosPanel';
import { ProductoDetalleModal } from '../../../../catalogo/components/ProductoDetalleModal';
import { ProductoCatalogoPublico } from '../../../../catalogo/types';

interface SeleccionarProductoModalProps {
  open: boolean;
  bolsaId: string;
  onClose: () => void;
  onProductoAgregado: (productoId: string, imagenUrl?: string | null) => Promise<void>;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

export function SeleccionarProductoModal({
  open,
  bolsaId,
  onClose,
  onProductoAgregado,
}: SeleccionarProductoModalProps) {
  const {
    productos,
    loading,
    loadingMore,
    hasMore,
    filtros,
    filtrosDisponibles,
    loadingFiltros,
    proveedorIds,
    subcategoriasSeleccionadas,
    cambiarFiltro,
    limpiarFiltros,
    cargarMas,
    setProveedores,
    toggleSubcategoria,
    toggleCategoriaCompleta,
    limpiarSubcategorias,
  } = useCatalogo();

  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
  const [productoDetalle, setProductoDetalle] = useState<ProductoCatalogoPublico | null>(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        cargarMas();
      }
    },
    [hasMore, loadingMore, loading, cargarMas],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(handleObserver, {
      root: gridRef.current,
      threshold: 0.1,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleAgregarProducto = useCallback(async (
    producto: ProductoCatalogoPublico,
    imagenUrl: string | null,
  ): Promise<boolean> => {
    try {
      const productoId = (producto.proveedorProductoId ?? producto.id!).toString();
      await onProductoAgregado(productoId, imagenUrl);
      setSnackbar({
        open: true,
        message: `"${producto.nombre}" agregado a la bolsa`,
        severity: 'success',
      });
      return true;
    } catch {
      setSnackbar({
        open: true,
        message: 'Error al agregar el producto',
        severity: 'error',
      });
      return false;
    }
  }, [onProductoAgregado]);

  const handleAbrirDetalle = useCallback((producto: ProductoCatalogoPublico) => {
    setProductoDetalle(producto);
    setDetalleOpen(true);
  }, []);

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        data-testid="modal-agregar-producto"
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            fontSize: 18,
            color: '#231F2B',
            borderBottom: '1px solid #DFDCE2',
            pb: 1.5,
          }}
        >
          Agregar producto a la bolsa
          <IconButton
            onClick={onClose}
            data-testid="btn-cerrar-modal-agregar"
            size="small"
            sx={{ color: '#57205F' }}
            aria-label="Cerrar modal"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            gap: 0,
            p: 0,
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {/* Sidebar de filtros */}
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              borderRight: '1px solid #DFDCE2',
              overflowY: 'auto',
              p: 2,
            }}
          >
            <FiltrosPanel
              filtros={filtros}
              filtrosDisponibles={filtrosDisponibles}
              loadingFiltros={loadingFiltros}
              proveedorIds={proveedorIds}
              subcategoriasSeleccionadas={subcategoriasSeleccionadas}
              productosCount={productos.length}
              onChange={cambiarFiltro}
              onSetProveedores={setProveedores}
              onToggleSubcategoria={toggleSubcategoria}
              onToggleCategoriaCompleta={toggleCategoriaCompleta}
              onLimpiarSubcategorias={limpiarSubcategorias}
              onLimpiar={limpiarFiltros}
            />
          </Box>

          {/* Grid principal */}
          <Box
            ref={gridRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Búsqueda por texto */}
            <TextField
              size="small"
              placeholder="Buscar productos..."
              value={filtros.texto}
              onChange={(e) => cambiarFiltro('texto', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#8A8F99', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: filtros.texto ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => cambiarFiltro('texto', '')}>
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&.Mui-focused fieldset': { borderColor: '#A3489D' },
                },
              }}
            />

            {/* Estado de carga inicial */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#A3489D' }} />
              </Box>
            )}

            {/* Grid de tarjetas */}
            {!loading && (
              <>
                {productos.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography sx={{ color: '#8A8F99', fontSize: 15 }}>
                      No se encontraron productos con los filtros aplicados
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    data-testid="modal-catalogo-grid"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 2,
                    }}
                  >
                    {productos.map((producto) => (
                      <CatalogoCard
                        key={producto.id}
                        producto={producto}
                        onClick={handleAbrirDetalle}
                        onAgregarClick={handleAgregarProducto}
                      />
                    ))}
                  </Box>
                )}

                {/* Sentinel para scroll infinito */}
                <div ref={sentinelRef} style={{ height: 1 }} />

                {loadingMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={28} sx={{ color: '#A3489D' }} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de detalle del producto (mismo del Catálogo) */}
      <ProductoDetalleModal
        open={detalleOpen}
        producto={productoDetalle}
        onClose={() => setDetalleOpen(false)}
        selectedBolsaId={bolsaId}
        onAgregar={async (prod, imagenUrl) => {
          const ok = await handleAgregarProducto(prod, imagenUrl);
          if (ok) setDetalleOpen(false);
        }}
      />

      {/* Snackbar de éxito */}
      <Snackbar
        open={snackbar.open && snackbar.severity === 'success'}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="snackbar-agregar-exito"
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Snackbar de error */}
      <Snackbar
        open={snackbar.open && snackbar.severity === 'error'}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="snackbar-agregar-error"
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
